import {truncate} from 'lodash';

import {TeammateAccountStatusesEnum, TeammateLicenseTypesEnum, TeammateModel} from '../api/teammateModel';

interface PartialTeammateModelForDisplayName {
  givenName: string;
  familyName: string;
  alias: string;
}

/** Compute the text to sort by for a teammate. */
export function buildTeammateSortKey(teammate: {givenName: string; familyName: string; alias: string}) {
  return (teammate.givenName + teammate.familyName).toLowerCase();
}

/** Checks whether or not the teammate is active. */
export function isTeammateActive(teammate: TeammateModel) {
  return (
    teammate.accountStatus === TeammateAccountStatusesEnum.ACTIVE &&
    teammate.licenseType !== TeammateLicenseTypesEnum.VISITOR &&
    teammate.licenseType !== TeammateLicenseTypesEnum.BOT
  );
}

export function isSandboxWorkspaceFakeTeammate(teammate: TeammateModel) {
  return teammate.licenseType === TeammateLicenseTypesEnum.VISITOR && teammate.isOnboardingDemo;
}

/** Checks whether or not the teammate is a visitor. */
export function isVisitorTeammate(teammate: TeammateModel) {
  // Treat the onboarding demo guest as a real teammate. They will only appear in demo conversations.
  return teammate.licenseType === TeammateLicenseTypesEnum.VISITOR && !teammate.isOnboardingDemo;
}

const csatTeammateAlias = 'front_csat';
const smartCsatTeammateAlias = 'front_smart_csat';
const companionAliasPrefix = 'front_companion';
const ruleAliasPrefix = 'front_rule_';
const macroAliasPrefix = 'front_macro_';
const playbookAliasPrefix = 'front_playbook_';
const apiAliasPrefix = 'front_api_';
const aiAliasPrefix = 'front_ai_';
// Autopilot rule + ai autoreply action + ai prompted reply action.
const aiAutopilotRuleAliasPrefix = `${aiAliasPrefix}autopilot`;
const chatbotAliasPrefix = 'front_chatbot_';
const aiSuggestedReplyAliasPrefix = `${aiAliasPrefix}suggested_reply`;
// Only for companies Front (11), EPD Test Company and Cloud Content Consulting.
const legacyAiSuggestedReplyAliasPrefix = `${aiAliasPrefix}_suggested_reply`;

export function isBotTeammate(teammate: TeammateModel) {
  return teammate.licenseType === TeammateLicenseTypesEnum.BOT;
}

/** Checks whether or not the teammate is the csat bot. */
export function isCsatTeammate(teammate: TeammateModel) {
  return isBotTeammate(teammate) && teammate.alias === csatTeammateAlias;
}

/** Checks whether or not the teammate is the smart csat bot. */
export function isSmartCsatTeammate(teammate: TeammateModel) {
  return isBotTeammate(teammate) && teammate.alias === smartCsatTeammateAlias;
}

function isRuleTeammateAlias(alias: string) {
  return alias.startsWith(ruleAliasPrefix);
}

function isMacroTeammateAlias(alias: string) {
  return alias.startsWith(macroAliasPrefix);
}

export function isMacroTeammate(teammate: TeammateModel) {
  return isBotTeammate(teammate) && isMacroTeammateAlias(teammate.alias);
}

function isPlaybookTeammateAlias(alias: string) {
  return alias.startsWith(playbookAliasPrefix);
}

export function isPlaybookTeammate(teammate: TeammateModel) {
  return isBotTeammate(teammate) && isPlaybookTeammateAlias(teammate.alias);
}

function isCompanionTeammateAlias(alias: string) {
  return alias.startsWith(companionAliasPrefix);
}

export function isCompanionTeammate(teammate: TeammateModel) {
  return isBotTeammate(teammate) && isCompanionTeammateAlias(teammate.alias);
}

export function isRuleTeammate(teammate: TeammateModel) {
  return isBotTeammate(teammate) && isRuleTeammateAlias(teammate.alias);
}

export function isApiTeammate(teammate: TeammateModel) {
  return isBotTeammate(teammate) && teammate.alias.startsWith(apiAliasPrefix);
}

export function isTemplateTeammate(teammate: TeammateModel) {
  return teammate.licenseType === TeammateLicenseTypesEnum.TEMPLATE;
}

export function isAiBotTeammate(teammate: TeammateModel) {
  return isBotTeammate(teammate) && teammate.alias.startsWith(aiAliasPrefix);
}

export function isAutopilotRuleTeammate(teammate: TeammateModel) {
  return isAiBotTeammate(teammate) && teammate.alias.startsWith(aiAutopilotRuleAliasPrefix);
}

export function isAiSuggestedReplyTeammate(teammate: TeammateModel) {
  return (
    isAiBotTeammate(teammate) &&
    (teammate.alias.startsWith(aiSuggestedReplyAliasPrefix) ||
      teammate.alias.startsWith(legacyAiSuggestedReplyAliasPrefix))
  );
}

// TODO (@tam-tanguy): Add suggested reply teammate. Keeping like this for now to keep a special author name for suggested replies.
export function isAutopilotTeammate(teammate: TeammateModel) {
  return isAutopilotRuleTeammate(teammate) || isPlaybookTeammate(teammate) || isCompanionTeammate(teammate);
}

export function isAiRelatedTeammate(teammate: TeammateModel) {
  return isAiBotTeammate(teammate) || isAutopilotTeammate(teammate) || isAiSuggestedReplyTeammate(teammate);
}

export function isChatbotTeammate(teammate: TeammateModel) {
  return isBotTeammate(teammate) && teammate.alias.startsWith(chatbotAliasPrefix);
}

export function isPreviewableTeammate(teammate: TeammateModel) {
  return (
    !isBotTeammate(teammate) ||
    isRuleTeammate(teammate) ||
    isMacroTeammate(teammate) ||
    isPlaybookTeammate(teammate)
  );
}

/** Compute the name to display for the specified teammate. */
export function buildTeammateDisplayName(teammate: PartialTeammateModelForDisplayName) {
  const name = `${teammate.givenName} ${teammate.familyName}`.trim();
  return name || teammate.alias;
}

/** Compute the short name to display for the specified teammate. */
export function buildTeammateShortDisplayName(teammate: PartialTeammateModelForDisplayName) {
  return teammate.givenName;
}

export function buildTeammateShortLastNameDisplayName(teammate: PartialTeammateModelForDisplayName) {
  return `${teammate.givenName} ${teammate.familyName.slice(0, 1) ?? ''}.`.trim();
}

/** Compute an (eventually) truncated name to display for the specified teammate. */
export function buildTeammateTruncatedDisplayName(
  teammate: PartialTeammateModelForDisplayName,
  maxLength: number,
) {
  const displayName = `${teammate.givenName}`.trim() || teammate.alias;
  return truncate(displayName, {length: maxLength, omission: '.'});
}

export function computeTeammateMatchQuery(teammate: TeammateModel, query: string) {
  const lowerCaseQuery = query.toLowerCase();

  // Match by email.
  const doesMatchByEmail = teammate.email.startsWith(lowerCaseQuery);

  // Match by name.
  const doesMatchByName = buildTeammateDisplayName(teammate).toLowerCase().includes(lowerCaseQuery);

  // Match by alias.
  const doesMatchByAlias = teammate.alias.toLowerCase().includes(lowerCaseQuery);

  return doesMatchByEmail || doesMatchByAlias || doesMatchByName;
}

function parseIdFromTeammateAlias(alias: string, prefix: string): number {
  const results = new RegExp(`^${prefix}_(\\d+)$`).exec(alias);

  if (!results) {
    throw new Error('Invalid bot teammate alias.');
  }

  return Number(results[1]);
}

export function parseRuleIdFromTeammateAlias(alias: string) {
  return parseIdFromTeammateAlias(alias, ruleAliasPrefix);
}

export function parseMacroIdFromTeammateAlias(alias: string) {
  return parseIdFromTeammateAlias(alias, macroAliasPrefix);
}

export function parsePlaybookIdFromTeammateAlias(alias: string) {
  return parseIdFromTeammateAlias(alias, playbookAliasPrefix);
}
